import React, { useState, useCallback, useRef, useEffect } from 'react';

export interface FilterOptions {
    productionCapability: string;
    handlingTier: string;
    status: string;
    recommendation: string;
}

export interface SortOption {
    field: 'pc' | 'tier' | 'current' | 'recommended' | 'modified' | 'recommended-date';
    direction: 'asc' | 'desc';
}

interface TableControlsProps {
    availablePCs: string[];
    availableTiers: string[];
    onFilterChange: (filters: FilterOptions) => void;
    onSortChange: (sort: SortOption) => void;
}

/**
 * Component that provides filtering and sorting controls for the hierarchical table
 */
export const TableControls: React.FC<TableControlsProps> = ({
    availablePCs,
    availableTiers,
    onFilterChange,
    onSortChange
}) => {
    // State for expanded/collapsed controls - default to collapsed
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const controlsRef = useRef<HTMLDivElement>(null);

    // Filter state
    const [filters, setFilters] = useState<FilterOptions>({
        productionCapability: '',
        handlingTier: '',
        status: '',
        recommendation: ''
    });

    // Sort state
    const [sortField, setSortField] = useState<SortOption['field']>('pc');
    const [sortDirection, setSortDirection] = useState<SortOption['direction']>('asc');
    
    // Track whether any filters are active
    const hasActiveFilters = 
        filters.productionCapability !== '' || 
        filters.handlingTier !== '' || 
        filters.status !== '' || 
        filters.recommendation !== '';
    
    // Toggle expanded state
    const toggleExpanded = () => {
        setIsExpanded(prev => !prev);
    };
    
    // Handle click outside to collapse
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (controlsRef.current && 
                !controlsRef.current.contains(event.target as Node) && 
                isExpanded) {
                setIsExpanded(false);
            }
        };
        
        // Only add listener if expanded
        if (isExpanded) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isExpanded]);
    
    // Handle filter changes
    const handleFilterChange = useCallback((key: keyof FilterOptions, value: string) => {
        const newFilters = {
            ...filters,
            [key]: value
        };
        
        setFilters(newFilters);
        onFilterChange(newFilters);
    }, [filters, onFilterChange]);
    
    // Handle sort changes
    const handleSortFieldChange = useCallback((field: SortOption['field']) => {
        setSortField(field);
        onSortChange({ field, direction: sortDirection });
    }, [sortDirection, onSortChange]);
    
    const handleSortDirectionChange = useCallback((direction: SortOption['direction']) => {
        setSortDirection(direction);
        onSortChange({ field: sortField, direction });
    }, [sortField, onSortChange]);
    
    return (
        <div className={`table-controls ${isExpanded ? 'expanded' : 'collapsed'}`} ref={controlsRef}>
            <div className="controls-header">
                <button 
                    className="toggle-controls-button" 
                    onClick={toggleExpanded}
                    aria-expanded={isExpanded}
                    aria-label={isExpanded ? "Collapse filters" : "Expand filters"}
                >
                    {isExpanded ? '▲ Hide Filters' : '▼ Show Filters'}
                </button>
                {!isExpanded && hasActiveFilters && (
                    <div className="active-filters-summary">
                        <span className="active-filters-label">Active filters:</span>
                        {filters.productionCapability && (
                            <span className="filter-pill">PC: {filters.productionCapability}</span>
                        )}
                        {filters.handlingTier && (
                            <span className="filter-pill">Tier: {filters.handlingTier}</span>
                        )}
                        {filters.status && (
                            <span className="filter-pill">Status: {filters.status}</span>
                        )}
                        {filters.recommendation && (
                            <span className="filter-pill">Rec: {filters.recommendation}</span>
                        )}
                    </div>
                )}
            </div>
            
            {isExpanded && (
                <div className="controls-content">
                    <div className="filter-controls">
                        <h3>Filter</h3>
                        <div className="filter-group">
                            <label htmlFor="pc-filter">Production Capability:</label>
                            <select 
                                id="pc-filter"
                                value={filters.productionCapability}
                                onChange={(e) => handleFilterChange('productionCapability', e.target.value)}
                            >
                                <option value="">All</option>
                                {availablePCs.map(pc => (
                                    <option key={pc} value={pc}>{pc}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label htmlFor="tier-filter">Handling Tier:</label>
                            <select 
                                id="tier-filter"
                                value={filters.handlingTier}
                                onChange={(e) => handleFilterChange('handlingTier', e.target.value)}
                            >
                                <option value="">All</option>
                                {availableTiers.map(tier => (
                                    <option key={tier} value={tier}>{tier}</option>
                                ))}
                            </select>
                        </div>

                        <div className="filter-group">
                            <label htmlFor="status-filter">Status:</label>
                            <select 
                                id="status-filter"
                                value={filters.status}
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="unchanged">Unchanged</option>
                                <option value="recommendation-accepted">Recommendation Accepted</option>
                                <option value="override-pending">Override Pending</option>
                            </select>
                        </div>

                        <div className="filter-group">
                            <label htmlFor="recommendation-filter">Recommendation:</label>
                            <select 
                                id="recommendation-filter"
                                value={filters.recommendation}
                                onChange={(e) => handleFilterChange('recommendation', e.target.value)}
                            >
                                <option value="">All</option>
                                <option value="higher">Higher Recommended</option>
                                <option value="lower">Lower Recommended</option>
                                <option value="none">No Recommendation</option>
                            </select>
                        </div>
                    </div>

                    <div className="sort-controls">
                        <h3>Sort By</h3>
                        <div className="sort-group">
                            <label htmlFor="sort-field">Field:</label>
                            <select 
                                id="sort-field"
                                value={sortField}
                                onChange={(e) => handleSortFieldChange(e.target.value as SortOption['field'])}
                            >
                                <option value="pc">Production Capability</option>
                                <option value="tier">Handling Tier</option>
                                <option value="current">Current GSLA</option>
                                <option value="recommended">Recommended GSLA</option>
                                <option value="modified">Last Modified</option>
                                <option value="recommended-date">Recommendation Date</option>
                            </select>
                        </div>

                        <div className="sort-group">
                            <label htmlFor="sort-direction">Order:</label>
                            <select 
                                id="sort-direction"
                                value={sortDirection}
                                onChange={(e) => handleSortDirectionChange(e.target.value as SortOption['direction'])}
                            >
                                <option value="asc">Ascending</option>
                                <option value="desc">Descending</option>
                            </select>
                        </div>
                    </div>

                    <div className="clear-filters">
                        <button 
                            onClick={() => {
                                const resetFilters = {
                                    productionCapability: '',
                                    handlingTier: '',
                                    status: '',
                                    recommendation: ''
                                };
                                setFilters(resetFilters);
                                onFilterChange(resetFilters);
                            }}
                        >
                            Clear Filters
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
